import type { Permission } from './permissions.js';
import { userHasPermission } from './permissions.js';

type AccessUser = {
  is_superadmin?: boolean | null;
  isRootAdmin?: boolean | null;
  role?: string | null;
  permissions?: readonly string[] | null;
  modulePortalAccess?: boolean;
} | null | undefined;

export type AppDestination = 'admin' | 'reports' | 'survey' | 'denied';

export const REPORTING_ENTRY_PERMISSIONS = [
  'dashboard.read',
  'doctor_report.read',
  'daily_clinical_report.read',
  'feedback_responses.read',
  'question_wise_response.read',
  'monthly_summary.read',
  'detailed_summary.read',
] as const satisfies readonly Permission[];

export const BUSINESS_REPORTS_ENTRY_PERMISSIONS = [
  'business_reports.view',
  'business_reports.manage',
  'business_reports.import',
  'business_reports.manual_revenue.view',
  'business_reports.manual_revenue.manage',
  'business_reports.approve_manual_entries',
  'business_reports.validation.view',
  'business_reports.validation.decide',
  'business_reports.targets.view',
  'business_reports.targets.create',
  'business_reports.targets.update',
  'business_reports.targets.export',
  'business_reports.target_governance.manage',
  'business_reports.ownership_governance.manage',
  'business_reports.export',
  'business_reports.waterfall.view',
  'business_reports.daily_revenue.view',
  'business_reports.medics_revenue.view',
  'business_reports.reconciliation.view',
  'business_reports.operations.view',
] as const satisfies readonly Permission[];

export function hasAdminAppAccess(user: AccessUser): boolean {
  return Boolean(user?.is_superadmin || user?.isRootAdmin || userHasPermission(user, 'admin_app.access'));
}

export function hasSurveyAppAccess(user: AccessUser): boolean {
  return userHasPermission(user, 'survey_app.use');
}

export function hasBusinessReportsPortalAccess(user: AccessUser): boolean {
  return BUSINESS_REPORTS_ENTRY_PERMISSIONS.some((permission) => userHasPermission(user, permission));
}

export function hasReportingPortalAccess(user: AccessUser): boolean {
  return (
    REPORTING_ENTRY_PERMISSIONS.some((permission) => userHasPermission(user, permission))
    || hasBusinessReportsPortalAccess(user)
    || user?.modulePortalAccess === true
  );
}

export function resolveAppDestination(user: AccessUser): AppDestination {
  if (hasAdminAppAccess(user)) return 'admin';
  if (hasReportingPortalAccess(user)) return 'reports';
  if (hasSurveyAppAccess(user)) return 'survey';
  return 'denied';
}
